// Categorized function library for the expression editors.
// mode "row"     = dataset-level calculated columns (advanced calc engine)
// mode "measure" = semantic calculated measures ([measure] refs, same engine in scalar mode)

const LOGIC = {
  category: "Logic",
  fns: [
    { name: "IF", sig: "IF(condition, then, else)", insert: "IF( , , )", desc: 'IF([Unit Price] > 100, "High", "Low")' },
    { name: "IIF", sig: "IIF(condition, then, else)", insert: "IIF( , , )", desc: "Same as IF (SQL Server style)" },
    { name: "IFS", sig: "IFS(cond1, val1, …, else)", insert: "IFS( , , )", desc: "First matching condition wins" },
    { name: "SWITCH", sig: "SWITCH(expr, val1, res1, …, default)", insert: "SWITCH( , , , )", desc: "Match a value against cases (CASE)" },
    { name: "CASE", sig: "CASE WHEN cond THEN res … ELSE d END", insert: "CASE WHEN  THEN  WHEN  THEN  ELSE  END", desc: "SQL-style conditional branches (any number of WHEN)" },
    { name: "IN", sig: "[field] IN ('a', 'b')", insert: " IN ( , )", desc: "Value is one of a list" },
    { name: "NOT IN", sig: "[field] NOT IN ('a', 'b')", insert: " NOT IN ( , )", desc: "Value is not in a list" },
    { name: "BETWEEN", sig: "[field] BETWEEN a AND b", insert: " BETWEEN  AND ", desc: "Inclusive range test" },
    { name: "AND", sig: "AND(a, b, …)", insert: "AND( , )", desc: "All conditions true" },
    { name: "OR", sig: "OR(a, b, …)", insert: "OR( , )", desc: "Any condition true" },
    { name: "NOT", sig: "NOT(x)", insert: "NOT()", desc: "Negate a condition" },
    { name: "ISBLANK", sig: "ISBLANK(x)", insert: "ISBLANK()", desc: "True when null or empty" },
    { name: "COALESCE", sig: "COALESCE(a, b, …)", insert: "COALESCE( , )", desc: "First non-null value" },
    { name: "IFNULL", sig: "IFNULL(x, fallback)", insert: "IFNULL( , )", desc: "Replace nulls with a fallback" },
    { name: "NULL", sig: "NULL", insert: "NULL", desc: "Blank value (e.g. IF(cond, [x], NULL))" },
  ],
};

const MATH = {
  category: "Math",
  fns: [
    { name: "ABS", sig: "ABS(x)", insert: "ABS()", desc: "Absolute value" },
    { name: "ROUND", sig: "ROUND(x, digits)", insert: "ROUND( , 2)", desc: "Round to digits" },
    { name: "CEILING", sig: "CEILING(x)", insert: "CEILING()", desc: "Round up" },
    { name: "FLOOR", sig: "FLOOR(x)", insert: "FLOOR()", desc: "Round down" },
    { name: "SQRT", sig: "SQRT(x)", insert: "SQRT()", desc: "Square root" },
    { name: "POWER", sig: "POWER(x, y)", insert: "POWER( , )", desc: "x to the power y" },
    { name: "EXP", sig: "EXP(x)", insert: "EXP()", desc: "e raised to x" },
    { name: "LN", sig: "LN(x)", insert: "LN()", desc: "Natural logarithm" },
    { name: "LOG10", sig: "LOG10(x)", insert: "LOG10()", desc: "Base-10 logarithm" },
    { name: "MOD", sig: "MOD(a, b)", insert: "MOD( , )", desc: "Remainder" },
    { name: "SIGN", sig: "SIGN(x)", insert: "SIGN()", desc: "-1, 0 or 1" },
    { name: "MIN", sig: "MIN(a, b)", insert: "MIN( , )", desc: "Smaller value (or column min)" },
    { name: "MAX", sig: "MAX(a, b)", insert: "MAX( , )", desc: "Larger value (or column max)" },
  ],
};

const TEXT = {
  category: "Text",
  fns: [
    { name: "CONCAT", sig: "CONCAT(a, b, …)", insert: "CONCAT( , )", desc: "Join text values" },
    { name: "UPPER", sig: "UPPER(s)", insert: "UPPER()", desc: "UPPERCASE" },
    { name: "LOWER", sig: "LOWER(s)", insert: "LOWER()", desc: "lowercase" },
    { name: "PROPER", sig: "PROPER(s)", insert: "PROPER()", desc: "Title Case" },
    { name: "TRIM", sig: "TRIM(s)", insert: "TRIM()", desc: "Strip spaces" },
    { name: "LEN", sig: "LEN(s)", insert: "LEN()", desc: "Text length" },
    { name: "LEFT", sig: "LEFT(s, n)", insert: "LEFT( , 3)", desc: "First n characters" },
    { name: "RIGHT", sig: "RIGHT(s, n)", insert: "RIGHT( , 3)", desc: "Last n characters" },
    { name: "MID", sig: "MID(s, start, len)", insert: "MID( , 1, 3)", desc: "Substring (1-based)" },
    { name: "REPLACE", sig: "REPLACE(s, old, new)", insert: "REPLACE( , , )", desc: "Replace text" },
    { name: "CONTAINS", sig: "CONTAINS(s, sub)", insert: "CONTAINS( , )", desc: "True when sub found" },
    { name: "STARTSWITH", sig: "STARTSWITH(s, sub)", insert: "STARTSWITH( , )", desc: "Begins with" },
    { name: "ENDSWITH", sig: "ENDSWITH(s, sub)", insert: "ENDSWITH( , )", desc: "Ends with" },
    { name: "SPLIT", sig: "SPLIT(s, delim, index)", insert: "SPLIT( , \",\", 1)", desc: "Nth part after splitting" },
    { name: "TEXT", sig: "TEXT(x)", insert: "TEXT()", desc: "Convert to text" },
    { name: "NUMBER", sig: "NUMBER(s)", insert: "NUMBER()", desc: "Convert to number" },
    { name: "CAST", sig: "CAST(x AS TEXT|INTEGER|DECIMAL|DATE|BOOLEAN)", insert: "CAST( AS TEXT)", desc: "SQL-style type conversion" },
  ],
};

const DATES = {
  category: "Date & time",
  fns: [
    { name: "EXTRACT", sig: "EXTRACT(YEAR FROM d)", insert: "EXTRACT(YEAR FROM )", desc: "SQL-style date part: YEAR, QUARTER, MONTH, WEEK, DAY…" },
    { name: "CURRENTYEAR", sig: "CURRENTYEAR()", insert: "CURRENTYEAR()", desc: "This calendar year (number)" },
    { name: "PREVIOUSYEAR", sig: "PREVIOUSYEAR()", insert: "PREVIOUSYEAR()", desc: "Last calendar year (number)" },
    { name: "CURRENTMONTH", sig: "CURRENTMONTH()", insert: "CURRENTMONTH()", desc: "Current month 1-12" },
    { name: "CURRENTQUARTER", sig: "CURRENTQUARTER()", insert: "CURRENTQUARTER()", desc: "Current quarter 1-4" },
    { name: "YEAR", sig: "YEAR(d)", insert: "YEAR()", desc: "Year number" },
    { name: "QUARTER", sig: "QUARTER(d)", insert: "QUARTER()", desc: "Quarter 1-4" },
    { name: "MONTH", sig: "MONTH(d)", insert: "MONTH()", desc: "Month 1-12" },
    { name: "WEEK", sig: "WEEK(d)", insert: "WEEK()", desc: "ISO week number" },
    { name: "DAY", sig: "DAY(d)", insert: "DAY()", desc: "Day of month" },
    { name: "WEEKDAY", sig: "WEEKDAY(d)", insert: "WEEKDAY()", desc: "1 = Monday … 7 = Sunday" },
    { name: "MONTHNAME", sig: "MONTHNAME(d)", insert: "MONTHNAME()", desc: "January, February…" },
    { name: "DAYNAME", sig: "DAYNAME(d)", insert: "DAYNAME()", desc: "Monday, Tuesday…" },
    { name: "TODAY", sig: "TODAY()", insert: "TODAY()", desc: "Current date" },
    { name: "NOW", sig: "NOW()", insert: "NOW()", desc: "Current date-time" },
    { name: "DATE", sig: "DATE(y, m, d)", insert: "DATE( , , )", desc: "Build a date" },
    { name: "DATEDIFF", sig: 'DATEDIFF("day", a, b)', insert: 'DATEDIFF("day", , )', desc: "Difference in a unit" },
    { name: "DATEADD", sig: 'DATEADD("month", n, d)', insert: 'DATEADD("month", 1, )', desc: "Shift a date" },
    { name: "DATETRUNC", sig: 'DATETRUNC("month", d)', insert: 'DATETRUNC("month", )', desc: "Truncate to period start" },
    { name: "TODATE", sig: "TODATE(s)", insert: "TODATE()", desc: "Parse text into a date" },
  ],
};

const AGG_ROW = {
  category: "Aggregates (whole column)",
  fns: [
    { name: "SUM", sig: "SUM([field])", insert: "SUM()", desc: "Column total — [Sales] / SUM([Sales]) * 100" },
    { name: "AVG", sig: "AVG([field])", insert: "AVG()", desc: "Column average" },
    { name: "COUNT", sig: "COUNT([field])", insert: "COUNT()", desc: "Non-null count" },
    { name: "COUNTD", sig: "COUNTD([field])", insert: "COUNTD()", desc: "Distinct count" },
    { name: "MEDIAN", sig: "MEDIAN([field])", insert: "MEDIAN()", desc: "Column median" },
    { name: "STDEV", sig: "STDEV([field])", insert: "STDEV()", desc: "Standard deviation" },
    { name: "VARIANCE", sig: "VARIANCE([field])", insert: "VARIANCE()", desc: "Variance" },
  ],
};

const AGG_MEASURE = {
  category: "Aggregates (over dataset columns)",
  fns: [
    { name: "SUM", sig: "SUM([column])", insert: "SUM()", desc: "Total — accepts a row expression: SUM(IF(cond, [amt], 0))" },
    { name: "AVG", sig: "AVG([column])", insert: "AVG()", desc: "Average" },
    { name: "COUNT", sig: "COUNT([column])", insert: "COUNT()", desc: "Non-null count" },
    { name: "COUNTD", sig: "COUNTD([column])", insert: "COUNTD()", desc: "Distinct count — COUNTD(IF([status] IN ('A','B'), [Dealer], NULL))" },
    { name: "MIN", sig: "MIN([column])", insert: "MIN()", desc: "Smallest value" },
    { name: "MAX", sig: "MAX([column])", insert: "MAX()", desc: "Largest value" },
    { name: "MEDIAN", sig: "MEDIAN([column])", insert: "MEDIAN()", desc: "Median" },
  ],
};

const OPERATORS = {
  category: "Operators",
  fns: [
    { name: "+", sig: "a + b", insert: " + ", desc: "Add (also joins text)" },
    { name: "-", sig: "a - b", insert: " - ", desc: "Subtract" },
    { name: "*", sig: "a * b", insert: " * ", desc: "Multiply" },
    { name: "/", sig: "a / b", insert: " / ", desc: "Divide (null on divide-by-zero)" },
    { name: "%", sig: "a % b", insert: " % ", desc: "Modulo" },
    { name: "^", sig: "a ^ b", insert: " ^ ", desc: "Power" },
    { name: "=", sig: "a = b", insert: " = ", desc: "Equals" },
    { name: "<>", sig: "a <> b", insert: " <> ", desc: "Not equals" },
    { name: "AND", sig: "a AND b", insert: " AND ", desc: "Both true" },
    { name: "OR", sig: "a OR b", insert: " OR ", desc: "Either true" },
  ],
};

const RECIPES_MEASURE = {
  category: "Recipes",
  fns: [
    { name: "Ratio", sig: "[num] / [den]", insert: "[] / []", desc: "Share / rate — e.g. profit margin" },
    { name: "Growth %", sig: "([cur] - [prev]) / [prev] * 100", insert: "([] - []) / [] * 100", desc: "Percent change" },
    { name: "Variance", sig: "[actual] - [target]", insert: "[] - []", desc: "Actual vs target gap" },
    { name: "Status", sig: 'IF([a] >= [b], "On track", "Behind")', insert: 'IF([] >= [], "On track", "Behind")', desc: "Conditional label" },
    { name: "Safe divide", sig: "IF([den] <> 0, [num]/[den], 0)", insert: "IF([] <> 0, [] / [], 0)", desc: "Guard divide-by-zero" },
    { name: "Conditional count", sig: "COUNTD(IF([status] IN ('A','B'), [id], NULL))", insert: "COUNTD(IF([] IN ( ), [], NULL))", desc: "Distinct count of rows matching a condition" },
    { name: "This-year total", sig: "SUM(IF(EXTRACT(YEAR FROM [date]) = CURRENTYEAR(), [amt], 0))", insert: "SUM(IF(EXTRACT(YEAR FROM []) = CURRENTYEAR(), [], 0))", desc: "Current-year total of a column" },
    { name: "Last-year total", sig: "SUM(IF(EXTRACT(YEAR FROM [date]) = PREVIOUSYEAR(), [amt], 0))", insert: "SUM(IF(EXTRACT(YEAR FROM []) = PREVIOUSYEAR(), [], 0))", desc: "Previous-year total of a column" },
  ],
};

export const ROW_FUNCTIONS = [LOGIC, AGG_ROW, MATH, TEXT, DATES, OPERATORS];
export const MEASURE_FUNCTIONS = [AGG_MEASURE, LOGIC, MATH, TEXT, DATES, RECIPES_MEASURE, OPERATORS];

const ALL_FN_NAMES = [
  "IF", "IIF", "WHERE", "IFS", "SWITCH", "CASE", "WHEN", "THEN", "ELSE", "END", "IN", "NOT", "BETWEEN", "IS", "NULL", "AND", "OR", "ISBLANK", "ISNULL", "COALESCE", "IFNULL",
  "SUM", "TOTAL", "AVG", "AVERAGE", "MEAN", "COUNT", "COUNTD", "DISTINCTCOUNT", "MEDIAN", "STDEV", "VARIANCE", "MIN", "MAX",
  "ABS", "SQRT", "EXP", "LN", "LOG10", "LOG", "CEILING", "CEIL", "FLOOR", "ROUND", "POWER", "POW", "MOD", "SIGN", "INT", "TRUNC",
  "UPPER", "LOWER", "PROPER", "TRIM", "LEN", "LENGTH", "LEFT", "RIGHT", "MID", "SUBSTRING", "CONCAT", "CONCATENATE",
  "REPLACE", "CONTAINS", "STARTSWITH", "ENDSWITH", "FIND", "SPLIT",
  "YEAR", "MONTH", "DAY", "QUARTER", "WEEK", "WEEKDAY", "HOUR", "MINUTE", "SECOND", "MONTHNAME", "DAYNAME",
  "TODAY", "NOW", "DATE", "DATEDIFF", "DATEADD", "DATETRUNC", "DATEVALUE", "TODATE", "DATEPART", "EXTRACT", "FROM",
  "CURRENTYEAR", "THISYEAR", "PREVIOUSYEAR", "LASTYEAR", "NEXTYEAR", "CURRENTMONTH", "CURRENTQUARTER", "CURRENTDAY",
  "NUMBER", "TONUMBER", "VALUE", "TEXT", "STR", "TOSTRING", "CAST", "CONVERT", "AS",
];
// Words that are keywords / operators, not callable functions: autocomplete never appends "()" to them.
export const KEYWORDS = new Set(["CASE", "WHEN", "THEN", "ELSE", "END", "IN", "NOT", "BETWEEN", "IS", "NULL", "AND", "OR", "FROM", "AS"]);

export const FN_NAMES = { row: ALL_FN_NAMES, measure: ALL_FN_NAMES };
